import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { CheckCircle2, ShieldCheck, TimerReset, Wrench } from "lucide-react";
import { Reveal } from "../components/common/Reveal";
import { companyInfo, featuredProjects, qualityItems, services } from "../data/siteData";

const heroImage =
  "https://static.prod-images.emergentagent.com/jobs/5995eb9b-583c-4baf-9d6a-d269b793de6b/images/2c0859068dcd0f03ecd1bfd66c09758b5264eca896d9b9672daa40a5738b1dee.png";

const iconMap = [TimerReset, CheckCircle2, Wrench, ShieldCheck, CheckCircle2];

export default function HomePage() {
  const [parallaxOffset, setParallaxOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => setParallaxOffset(Math.min(window.scrollY * 0.2, 120));
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div data-testid="home-page" className="page-shell">
      <section
        data-testid="home-hero-section"
        className="hero-section"
        style={{ backgroundImage: `linear-gradient(rgba(10,19,31,0.72), rgba(10,19,31,0.82)), url(${heroImage})`, backgroundPosition: `center calc(50% + ${parallaxOffset}px)` }}
      >
        <div className="container-shell hero-content">
          <p data-testid="hero-tagline" className="eyebrow">{companyInfo.tagline}</p>
          <h1 data-testid="hero-heading" className="hero-title">Building Houston’s Structural Strength Since 1978</h1>
          <p data-testid="hero-subheading" className="hero-description">
            Precision-driven structural steel fabrication for commercial, residential, and industrial construction across Texas.
          </p>
          <div className="hero-actions">
            <Link to="/contact#quote-form" data-testid="hero-request-quote-button" className="primary-btn">Request a Quote</Link>
            <a href="#featured-projects" data-testid="hero-view-work-button" className="secondary-btn">View Our Work</a>
          </div>
        </div>
      </section>

      <Reveal>
        <section data-testid="legacy-section" className="section-block">
          <div className="container-shell two-column">
            <div>
              <p data-testid="legacy-eyebrow" className="eyebrow">Legacy & Capability</p>
              <h2 data-testid="legacy-heading" className="section-title">Decades of disciplined structural steel execution in Houston.</h2>
            </div>
            <p data-testid="legacy-description" className="section-paragraph">
              Since 1978, Wayne Iron Works has operated as a Houston-based fabrication partner for contractors, developers, and industrial teams who demand structural accuracy and predictable execution. Our engineers, detailers, and certified welders work as one integrated team—producing steel packages that arrive field-ready, aligned to engineered intent, and delivered with long-term accountability.
              <br />
              <br />
              Wayne Iron Works operates under the leadership of owner Alex Wayne, continuing a long-standing commitment to structural steel excellence.
            </p>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section data-testid="services-section" className="section-block muted-bg">
          <div className="container-shell">
            <p data-testid="services-eyebrow" className="eyebrow">Core Services</p>
            <h2 data-testid="services-heading" className="section-title">Fabrication solutions engineered for structural confidence.</h2>
            <div className="services-grid">
              {services.map((service) => (
                <article key={service.title} data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, "-")}`} className="service-card">
                  <h3 data-testid={`service-title-${service.title.toLowerCase().replace(/\s+/g, "-")}`}>{service.title}</h3>
                  <p data-testid={`service-description-${service.title.toLowerCase().replace(/\s+/g, "-")}`}>{service.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section id="featured-projects" data-testid="featured-projects-section" className="section-block">
          <div className="container-shell">
            <p data-testid="featured-projects-eyebrow" className="eyebrow">Featured Projects</p>
            <h2 data-testid="featured-projects-heading" className="section-title">Selected steel programs delivered with precision and control.</h2>
            <div className="projects-grid">
              {featuredProjects.map((project) => (
                <article key={project.slug} data-testid={`featured-project-card-${project.slug}`} className="project-card">
                  <div className="project-image-wrap">
                    <img data-testid={`featured-project-image-${project.slug}`} src={project.image} alt={project.title} className="project-image" />
                  </div>
                  <div className="project-info">
                    <p data-testid={`featured-project-sector-${project.slug}`} className="project-sector">{project.sector}</p>
                    <h3 data-testid={`featured-project-title-${project.slug}`}>{project.title}</h3>
                    <Link to={`/projects/${project.slug}`} data-testid={`featured-project-link-${project.slug}`} className="text-link">View Project Details</Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section data-testid="quality-safety-section" className="section-block muted-bg">
          <div className="container-shell">
            <p data-testid="quality-eyebrow" className="eyebrow">Quality & Safety</p>
            <h2 data-testid="quality-heading" className="section-title">Built on cost control, schedule discipline, and safety-first execution.</h2>
            <div className="quality-grid">
              {qualityItems.map((item, index) => {
                const Icon = iconMap[index % iconMap.length];
                return (
                  <article key={item.title} data-testid={`quality-item-${item.title.toLowerCase().replace(/\s+/g, "-")}`} className="quality-card">
                    <Icon size={18} className="quality-icon" />
                    <h3 data-testid={`quality-title-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>{item.title}</h3>
                    <p data-testid={`quality-description-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>{item.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      </Reveal>

      <section data-testid="closing-cta-section" className="cta-band">
        <div className="container-shell cta-band-inner">
          <h2 data-testid="closing-cta-heading">Engineered to Perform. Built to Endure.</h2>
          <Link to="/contact#quote-form" data-testid="closing-cta-button" className="primary-btn">Request a Quote</Link>
        </div>
      </section>
    </div>
  );
}
